<template>
  <div>
    <el-card>
      <!-- 表单里只有「角色名称」一个输入框，按回车浏览器会提交表单、整页刷新，得拦掉 -->
      <el-form class="form_pad" :inline="true" @submit.native.prevent>
        <el-row class="clear_fix">
          <el-form-item :label="$t('subAccount.role.name')">
            <el-input
              v-model.trim="filters.name"
              :style="{ width: nameWidth }"
              :placeholder="$t('subAccount.role.namePh')"
            />
          </el-form-item>
          <el-form-item :label="$t('subAccount.role.menuPerm')">
            <!-- 多选、命中即算（并集）。选项来自登录下发的菜单树 -->
            <multi-select
              v-model="filters.menuKeys"
              :groups="menuGroups"
              :placeholder="$t('common.all')"
              width="auto"
              :min-width="180"
            />
          </el-form-item>
          <el-form-item :label="$t('subAccount.role.appPerm')">
            <multi-select
              v-model="filters.apps"
              :options="appOptions"
              :placeholder="$t('common.all')"
              width="auto"
              :min-width="180"
              :panel-width="640"
            />
          </el-form-item>
        </el-row>
        <el-row class="search-btns">
          <el-button type="primary" icon="el-icon-search" @click="search">{{ $t('common.search') }}</el-button>
        </el-row>
      </el-form>
    </el-card>

    <div class="mb10">
      <el-button type="primary" class="handle-del mr10" @click="create">{{ $t('subAccount.role.create') }}</el-button>
    </div>

    <el-card>
      <div class="table-container">
        <el-table :data="list" class="table" v-loading="loading">
          <el-table-column prop="name" :label="$t('subAccount.role.name')" min-width="120" align="left" />

          <el-table-column :label="$t('subAccount.role.menuPerm')" min-width="340" align="left">
            <template slot-scope="scope">
              <el-tag v-for="(t, i) in menuBadges(scope.row).shown" :key="i" type="info" size="small" class="mr10">{{ t }}</el-tag>
              <!-- 超过 4 项只摆前 3 个，剩下的悬浮看 —— 一行塞十几个标签会把表格撑变形 -->
              <el-popover
                v-if="menuBadges(scope.row).restCount"
                trigger="hover"
                placement="bottom-start"
                width="300"
                popper-class="role-perm-pop"
              >
                <!-- 菜单总共就十来个，不需要滚动容器；往下弹，不盖住这一行自己的标签 -->
                <p class="pop-title">{{ $t('subAccount.role.menuPermCount', { n: menuBadges(scope.row).all.length }) }}</p>
                <div class="pop-grid">
                  <span v-for="(t, i) in menuBadges(scope.row).all" :key="i" :title="t">{{ t }}</span>
                </div>
                <span slot="reference" class="hover-more">
                  {{ $t('subAccount.role.more', { n: menuBadges(scope.row).restCount, total: menuBadges(scope.row).all.length }) }}
                </span>
              </el-popover>
            </template>
          </el-table-column>

          <el-table-column :label="$t('subAccount.role.appPerm')" min-width="140" align="left">
            <template slot-scope="scope">
              <span v-if="scope.row.is_all_apps">{{ $t('subAccount.role.allApps') }}</span>
              <el-popover
                v-else
                trigger="hover"
                placement="bottom-start"
                width="300"
                popper-class="role-perm-pop"
              >
                <p class="pop-title">{{ $t('subAccount.role.appPermCount', { n: (scope.row.app_names || []).length }) }}</p>
                <!-- 应用可能很多，超出就滚动 -->
                <div class="pop-list">
                  <div v-for="(n, i) in (scope.row.app_names || [])" :key="i" :title="n">{{ n }}</div>
                </div>
                <span slot="reference" class="hover-more">
                  {{ $t('subAccount.role.someAppsCount', { n: (scope.row.app_names || []).length }) }}
                </span>
              </el-popover>
            </template>
          </el-table-column>

          <el-table-column :label="$t('subAccount.role.accountCount')" width="100" align="left">
            <template slot-scope="scope">
              <el-button
                type="text"
                size="large"
                :disabled="!scope.row.account_count"
                @click="$emit('go-accounts', scope.row.id, scope.row.name)"
              >{{ scope.row.account_count || 0 }}</el-button>
            </template>
          </el-table-column>

          <el-table-column :label="$t('subAccount.role.createdAt')" width="150" align="left">
            <template slot-scope="scope">{{ datetime(scope.row.create_time) }}</template>
          </el-table-column>
          <el-table-column :label="$t('subAccount.role.updatedAt')" width="150" align="left">
            <template slot-scope="scope">{{ datetime(scope.row.update_time) }}</template>
          </el-table-column>

          <el-table-column :label="$t('table.operation')" width="130" align="left">
            <template slot-scope="scope">
              <el-button type="text" size="large" @click="edit(scope.row)">{{ $t('operation.edit') }}</el-button>
              <el-tooltip
                :disabled="!scope.row.account_count"
                effect="dark"
                placement="top"
                :content="$t('subAccount.role.deleteBlocked', { n: scope.row.account_count })"
              >
                <span class="tip-wrap">
                  <el-button
                    type="text"
                    size="large"
                    :disabled="scope.row.account_count > 0"
                    @click="remove(scope.row)"
                  >{{ $t('operation.delete') }}</el-button>
                </span>
              </el-tooltip>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination">
          <el-pagination
            background
            :current-page="query.page"
            :page-size="query.page_size"
            layout="total, sizes, prev, pager, next"
            :total="total"
            @current-change="handlePage"
            @size-change="handlePageSize"
          />
        </div>
      </div>
    </el-card>

    <role-dialog
      v-if="dialogVisible"
      :visible.sync="dialogVisible"
      :role="editing"
      :apps="apps"
      :apps-loading="appsLoading"
      :menu-tree="menuTree"
      @saved="onSaved"
    />
  </div>
</template>

<script>
import perm from '@/utils/permission'
import { datetime } from '@/utils/subAccount/format'
import { fitWidth } from '@/utils/subAccount/fitWidth'
import MultiSelect from '@/views/abroad/components/MultiSelect'
import RoleDialog from './RoleDialog'

const ALL_APPS = 'ALL'
const BADGE_MAX = 4
const BADGE_SHOWN = 3

export default {
  name: 'SubAccountRoleList',
  components: { MultiSelect, RoleDialog },
  data() {
    return {
      loading: false,
      appsLoading: false,
      list: [],
      total: 0,
      apps: [],
      // 可授权菜单树，来自 menu/permissionTree。跟 login 下发的导航树不是一回事：
      // 导航树是「当前账号能看到什么」，这棵是「建角色时能授权什么」。
      // 菜单筛选项和角色弹窗的选择器共用这一份
      menuTree: [],
      // filters 是面板上的草稿，点「查询」才复制进 query 发请求（PRD 4.1.1 第 1.e 条）
      filters: { name: '', menuKeys: [], apps: [] },
      query: { page: 1, page_size: 20 },
      dialogVisible: false,
      editing: null
    }
  },
  computed: {
    // 按提示文字撑宽，换语言时跟着重算
    nameWidth() {
      return fitWidth(this.$t('subAccount.role.namePh'), { min: 160 })
    },
    // 菜单筛选的分组选项：一级菜单本身就是权限位时（数据）自己当唯一子项，
    // MultiSelect 的分组结构要求每组都得有 children
    menuGroups() {
      return this.menuTree.map(top => {
        const children = top.children && top.children.length ? top.children : [top]
        return {
          value: perm.menuKey(top),
          label: perm.menuLabel(top),
          children: children.map(c => ({ value: perm.menuKey(c), label: perm.menuLabel(c) }))
        }
      })
    },
    // 可授权权限位总数，用来判断一个角色是不是「全部菜单」
    menuLeafCount() {
      let n = 0
      this.menuTree.forEach(top => {
        n += top.children && top.children.length ? top.children.length : 1
      })
      return n
    },
    // 应用权限的筛选项 = 「全部应用」+ 所有应用名。后端的 app_scope 是一个
    // 字符串数组，"ALL" 命中 is_all_apps 的角色，其余按 application_id 匹配白名单
    appOptions() {
      const out = [{ value: ALL_APPS, label: this.$t('subAccount.role.allApps') }]
      this.apps.forEach(a => out.push({ value: a.id, label: a.name }))
      return out
    }
  },
  // 只拉筛选项，列表数据由父页面在切到本 tab 时拉
  created() {
    this.loadMenuTree()
    this.loadApps()
  },
  methods: {
    datetime: datetime,
    // 可授权菜单树。后端返回的形状认两种：包一层 list 的和直接给数组的
    loadMenuTree() {
      this.__getData('menuPermissionTree', null, res => {
        if (res && res.code == 1) {
          this.$message.error(res.msg)
          return
        }
        this.menuTree = (res && res.list) || (Array.isArray(res) ? res : [])
      }, errmsg => {
        this.$message.error(errmsg)
      })
    },
    // 应用选择器/筛选的候选项复用屏蔽管理那个应用列表接口 —— 主账号在这个页面
    // 本来就能看到全部应用，不为选择器单开接口
    loadApps() {
      this.appsLoading = true
      this.__getData('getAppScopeList', null, res => {
        this.appsLoading = false
        if (res && res.code == 1) {
          this.$message.error(res.msg)
          return
        }
        this.apps = (res && res.list) || []
      }, errmsg => {
        this.appsLoading = false
        this.$message.error(errmsg)
      // HTTP 层挂了的时候 ajax 只 reject，fn / errorFn 都不走，转圈得自己收一下。
      // 提示拦截器已经弹过了，这里只关 loading，不重复弹
      }).catch(() => {
        this.appsLoading = false
      })
    },
    buildParams() {
      const f = this.filters
      const params = {
        page: this.query.page,
        page_size: this.query.page_size
      }
      if (f.name) params.name = f.name
      if (f.menuKeys.length) params.menu_keys = f.menuKeys.slice()
      // "ALL" 和具体的 application_id 混在同一个数组里发，后端自己分辨
      if (f.apps.length) params.app_scope = f.apps.map(String)
      return params
    },
    getRoleList() {
      this.loading = true
      this.__postData('roleList', this.buildParams(), res => {
        this.loading = false
        if (res && res.code == 1) {
          this.$message.error(res.msg)
          return
        }
        this.list = (res && res.list) || []
        this.total = (res && res.total) || 0
      }, errmsg => {
        this.loading = false
        this.$message.error(errmsg)
      }).catch(() => {
        this.loading = false
      })
    },
    search() {
      this.query.page = 1
      this.getRoleList()
    },
    handlePage(p) {
      this.query.page = p
      this.getRoleList()
    },
    handlePageSize(size) {
      this.query.page = 1
      this.query.page_size = size
      this.getRoleList()
    },
    menuBadges(row) {
      const all = perm.roleMenuLabels(row)
      // 全都授权了就不用一个个列，一个「全部菜单」说得更清楚
      if (all.length && this.menuLeafCount && all.length === this.menuLeafCount) {
        return { all: all, shown: [this.$t('subAccount.role.allMenus')], restCount: 0 }
      }
      if (all.length <= BADGE_MAX) return { all: all, shown: all, restCount: 0 }
      return { all: all, shown: all.slice(0, BADGE_SHOWN), restCount: all.length - BADGE_SHOWN }
    },
    create() {
      this.editing = null
      this.dialogVisible = true
    },
    edit(row) {
      this.editing = row
      this.dialogVisible = true
    },
    onSaved() {
      this.getRoleList()
    },
    remove(row) {
      if (row.account_count > 0) return
      this.$confirm(
        this.$t('subAccount.role.deleteBody', { name: row.name }),
        this.$t('subAccount.role.deleteTitle'),
        {
          confirmButtonText: this.$t('subAccount.role.confirmDelete'),
          cancelButtonText: this.$t('cancel'),
          type: 'warning'
        }
      )
        .then(() => {
          this.__postData('roleDelete', { id: row.id }, res => {
            if (res && res.code == 1) {
              this.$message.error(res.msg)
              return
            }
            this.$message.success(this.$t('subAccount.role.deleted'))
            // 删的是这一页最后一条：退回上一页，不然停在一个空页上
            if (this.list.length === 1 && this.query.page > 1) this.query.page--
            this.getRoleList()
          }, errmsg => {
            this.$message.error(errmsg)
          })
        })
        // 确认框点「取消」也走 reject，这里必须接住，不然控制台一片未捕获的 cancel
        .catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
/* 文字的字号、字重走全局；悬浮层里标题灰、内容黑，分出层次 */
/* 应用多时仍可滚轮翻，但不显示滚动条 */
.pop-list {
  max-height: 240px;
  overflow-y: auto;
  scrollbar-width: none;
  color: #303133;

  &::-webkit-scrollbar {
    display: none;
  }

  div {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  div + div {
    margin-top: 10px;
  }
}
/* 「等 7 项」「指定 3 个应用」：虚线下划线提示可以悬浮看全部 */
.hover-more {
  border-bottom: 1px dashed currentColor;
  cursor: default;
}
.pop-title {
  margin: 0 0 12px;
  color: #909399;
}
/* 悬浮层里的菜单两列排，名字太长省略，悬浮看全 */
.pop-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 16px;
  color: #303133;

  span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}
.tip-wrap {
  display: inline-block;
}
</style>

<style lang="scss">
/* popover 挂在 body 下，scoped 样式管不到外壳，只能单独写一份全局的 */
.role-perm-pop.el-popover {
  padding: 16px 20px;
  border: none;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(31, 35, 41, 0.12);
}
</style>
